import { useInfiniteQuery, useQueries, useQuery } from 'react-query';
import { HeroConversationMessage } from '../../model';
import { useGetTwilioClient } from './twilioClientQueries';
import { Message } from '@twilio/conversations';
import { useGetActiveHeroConversations, useGetContactConversations } from '../hero';
import { useContext } from 'react';
import { AuthenticationContext } from '../../components';

const MESSAGES_PAGE_SIZE = 50;

type UnreadMessageCount = {
  data: {
    totalCount: number;
    countPerConversation: Record<string, number>;
  },
};

export const useGetTwilioConversationById = (conversationId?: string) => {
  const { isAuthenticated } = useContext(AuthenticationContext);
  const { data: twilioClient } = useGetTwilioClient();
  return useQuery(
    ['getTwilioConversation', conversationId],
    () => {
      if (!conversationId) {
        return;
      }
      return twilioClient?.getConversationBySid(conversationId);
    },
    {
      suspense: true,
      enabled: Boolean(twilioClient) && isAuthenticated,
      staleTime: Infinity,
    },
  );
};

export const useGetContactConversationsUnreadMessagesCount = (): UnreadMessageCount => {
  const { isAuthenticated } = useContext(AuthenticationContext);
  const { data: twilioClient } = useGetTwilioClient();
  const { data: contactConversations = [] } = useGetContactConversations();

  const queryResults = useQueries(
    contactConversations.map(({ twilioConversationId }) => ({
        queryKey: [
          'getTwilioConversationUnreadMessagesCount',
          twilioConversationId,
        ],
        queryFn: async () => {
          const twilioConversation = await twilioClient?.getConversationBySid(
            twilioConversationId,
          );
          return twilioConversation?.getUnreadMessagesCount();
        },
        staleTime: Infinity,
        enabled: isAuthenticated,
      }))
  );

  const totalCount = queryResults.reduce((totalCount, result) => {
    if (!result.data) {
      return totalCount;
    }
    return totalCount + result.data;
  }, 0);

  return {
    data: {
      totalCount,
      countPerConversation: queryResults.reduce((acc, item, index) => {
        if (!item?.data) {
          return acc;
        }
        const contactConversation = contactConversations[index];
        return {
          ...acc,
          [contactConversation.id]: item.data,
        };
      }, {}),
    },
  };
};

export const useGetActiveConversationsUnreadMessagesCount = (): UnreadMessageCount => {
  const { isAuthenticated } = useContext(AuthenticationContext);
  const { data: twilioClient } = useGetTwilioClient();
  const { data: heroConversations = [] } = useGetActiveHeroConversations();

  const queryResults = useQueries(
    heroConversations.map((conversation) => ({
        queryKey: [
          'getTwilioConversationUnreadMessagesCount',
          conversation.twilioConversationId,
        ],
        queryFn: async () => {
          const twilioConversation = await twilioClient?.getConversationBySid(
            conversation.twilioConversationId,
          );
          return twilioConversation?.getUnreadMessagesCount();
        },
        staleTime: Infinity,
        enabled: isAuthenticated,
      }))
  );

  const totalCount = queryResults.reduce((totalCount, result) => {
    if (!result.data) {
      return totalCount;
    }
    return totalCount + result.data;
  }, 0);

  return {
    data: {
      totalCount,
      countPerConversation: queryResults.reduce((acc, item, index) => {
        const heroConversation = heroConversations[index];
        return {
          ...acc,
          [heroConversation.id]: item.data,
        };
      }, {}),
    },
  };
};

export const useGetTwilioConversationUnreadMessagesCount = (
  conversationId?: string,
) => {
  const { data: conversation } = useGetTwilioConversationById(conversationId);
  return useQuery(
    ['getTwilioConversationUnreadMessagesCount', conversationId],
    async () => conversation?.getUnreadMessagesCount(),
    {
      staleTime: Infinity,
      enabled: Boolean(conversation),
      suspense: true,
    },
  );
};

export const useGetTwilioConversationMessages = (conversationId?: string) => {
  const { data: conversation } = useGetTwilioConversationById(conversationId);
  return useInfiniteQuery(
    ['getTwilioConversationMessages', conversationId],
    async ({ pageParam }) => {
      if (!conversation) {
        return [];
      }

      const messagePaginator = await conversation.getMessages(
        MESSAGES_PAGE_SIZE,
        pageParam,
      );

      return messagePaginator?.items as (Message & HeroConversationMessage)[];
    },
    {
      enabled: Boolean(conversation),
      getNextPageParam: (lastPage) => {
        const firstMessageOfLastPage = lastPage[0];
        if (firstMessageOfLastPage?.index) {
          return firstMessageOfLastPage.index - 1;
        }
      },
      staleTime: Infinity,
      suspense: true,
    },
  );
};

export const useGetTwilioConversationMessageMediaUrl = (
  message: Message,
  enabled: boolean,
) => {
  return useQuery(
    ['getTwilioConversationMessageMediaUrl', message.sid],
    async () => {
      const media = message.attachedMedia?.[0];
      if (!media) {
        return;
      }
      return media.getContentTemporaryUrl();
    },
    {
      enabled,
      refetchOnWindowFocus: false,
    },
  );
};

export const useGetIsShopperTyping = (conversationId?: string) => {
  return useQuery<boolean>(
    ['getIsShopperTyping', conversationId],
    () => {
      return false;
    },
    { staleTime: Infinity },
  );
};
